/**
 * custom-demo-booking.js
 * Shared CCC / Phaser feature (phaser: shared) — booking dialog for the
 * In-Store Demo Notice block (blocks/custom-demo-notice.liquid).
 *
 * The card's "Book a demonstration" button opens a theme-native
 * <dialog class="dialog-modal"> holding a Shopify {% form 'contact' %}.
 * The customer picks up to three preferred appointment slots (native date
 * input + a time-window select) which are stored as chips backed by hidden
 * contact[Preferred time N] inputs, so the request arrives through Shopify's
 * built-in contact email with the slots as labelled lines.
 *
 * Dialog conventions copied from custom-shipping-estimate.js (the theme's
 * proven path):
 * - reparent the <dialog> onto document.body before first showModal() so an
 *   ancestor's display quirks can't break top-layer/closed-state behaviour
 * - lockScroll/unlockScroll from @theme/utilities
 * - close via the theme's .dialog-closing animation, raced with a timeout so
 *   reduced-motion can't hang the close and leave scroll locked
 */

import { lockScroll, unlockScroll, onAnimationEnd } from '@theme/utilities';

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const MAX_SLOTS = 3;
const BOOKING_WINDOW_DAYS = 30;

class DemoBooking extends HTMLElement {
  connectedCallback() {
    this._dialog = this.querySelector('[data-ccdm-dialog]');
    this._openBtn = this.querySelector('[data-ccdm-open]');
    if (!this._dialog || !this._openBtn) return;

    this._form = this._dialog.querySelector('form');
    this._date = this._dialog.querySelector('[data-ccdm-date]');
    this._time = this._dialog.querySelector('[data-ccdm-time]');
    this._addBtn = this._dialog.querySelector('[data-ccdm-add]');
    this._chips = this._dialog.querySelector('[data-ccdm-chips]');
    this._slotsWrap = this._dialog.querySelector('[data-ccdm-slots]');
    this._hint = this._dialog.querySelector('[data-ccdm-hint]');
    this._slotInputs = Array.from(this._dialog.querySelectorAll('[data-ccdm-slot-input]'));
    this._closedDays = (this._dialog.dataset.closedDays || '')
      .split(',')
      .map((d) => parseInt(d.trim(), 10))
      .filter((d) => !Number.isNaN(d));

    this._slots = [];

    this._initDateBounds();

    this._openBtn.addEventListener('click', (event) => {
      event.preventDefault();
      this._open();
    });
    this._dialog.addEventListener('click', (event) => {
      if (event.target instanceof Element && event.target.closest('[data-ccdm-close]')) {
        this._close();
        return;
      }
      // Light-dismiss on backdrop click: the dialog element itself is the
      // target only when the click lands outside the inner panel.
      if (event.target === this._dialog) { this._close(); return; }
      // Clicking anywhere else closes an open calendar (except the calendar
      // itself or its field, which manage their own state).
      if (this._cal && !this._cal.hidden && event.target instanceof Element
          && !event.target.closest('[data-ccdm-cal]') && event.target !== this._date) {
        this._cal.hidden = true;
      }
    });
    this._dialog.addEventListener('cancel', (event) => {
      // Esc: run the animated close instead of the instant UA close.
      event.preventDefault();
      this._close();
    });

    this._addBtn?.addEventListener('click', () => this._addSlot());
    // Custom inline calendar: weekends/closed days render greyed-out and
    // unclickable (the native picker can't disable days of the week).
    this._cal = this._dialog.querySelector('[data-ccdm-cal]');
    this._selectedDate = null;
    this._viewMonth = null;
    this._date?.addEventListener('click', () => this._toggleCal());
    this._date?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this._toggleCal(); }
    });
    this._form?.addEventListener('submit', (event) => this._guardSubmit(event));

    // Server-rendered success or error state (present only when this form's
    // contact[id] matched the POST): reopen the dialog so the customer sees
    // the outcome of their submit after the page reload.
    if (this._dialog.querySelector('.ccdm-outcome')) {
      this._open();
    }
  }

  disconnectedCallback() {
    if (this._dialog?.open) {
      unlockScroll(this._dialog);
      this._dialog.close();
    }
    // Put an adopted dialog back where Liquid rendered it.
    if (this._dialogHosted && this._dialogHome?.parent) {
      this._dialogHome.parent.insertBefore(this._dialog, this._dialogHome.next);
      this._dialogHosted = false;
    }
  }

  _initDateBounds() {
    const min = new Date();
    min.setHours(0, 0, 0, 0);
    min.setDate(min.getDate() + 1);
    const max = new Date();
    max.setHours(0, 0, 0, 0);
    max.setDate(max.getDate() + BOOKING_WINDOW_DAYS);
    this._minDate = min;
    this._maxDate = max;
  }

  _toggleCal() {
    if (!this._cal) return;
    if (this._cal.hidden) {
      this._viewMonth = new Date(
        (this._selectedDate || this._minDate).getFullYear(),
        (this._selectedDate || this._minDate).getMonth(),
        1
      );
      this._renderCal();
      this._cal.hidden = false;
      // The dialog body scrolls — make sure the whole calendar is on screen.
      requestAnimationFrame(() => this._cal.scrollIntoView({ block: 'nearest', behavior: 'smooth' }));
    } else {
      this._cal.hidden = true;
    }
  }

  _selectable(d) {
    return d >= this._minDate && d <= this._maxDate && !this._closedDays.includes(d.getDay());
  }

  _renderCal() {
    const cal = this._cal;
    cal.textContent = '';
    const view = this._viewMonth;

    const head = document.createElement('div');
    head.className = 'ccdm-cal__head';
    const label = document.createElement('span');
    label.className = 'ccdm-cal__month';
    label.textContent = view.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
    const nav = document.createElement('span');
    nav.className = 'ccdm-cal__nav';
    const mk = (txt, delta, disabled) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.textContent = txt;
      b.disabled = disabled;
      b.setAttribute('aria-label', delta < 0 ? 'Previous month' : 'Next month');
      b.addEventListener('click', () => {
        this._viewMonth = new Date(view.getFullYear(), view.getMonth() + delta, 1);
        this._renderCal();
      });
      return b;
    };
    const firstOfView = new Date(view.getFullYear(), view.getMonth(), 1);
    const minMonth = new Date(this._minDate.getFullYear(), this._minDate.getMonth(), 1);
    const maxMonth = new Date(this._maxDate.getFullYear(), this._maxDate.getMonth(), 1);
    nav.appendChild(mk('‹', -1, firstOfView <= minMonth));
    nav.appendChild(mk('›', 1, firstOfView >= maxMonth));
    head.appendChild(label);
    head.appendChild(nav);
    cal.appendChild(head);

    const grid = document.createElement('div');
    grid.className = 'ccdm-cal__grid';
    grid.setAttribute('role', 'grid');
    for (const d of ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']) {
      const el = document.createElement('span');
      el.className = 'ccdm-cal__dow';
      el.textContent = d;
      grid.appendChild(el);
    }
    // Monday-first offset for the 1st of the month
    const first = new Date(view.getFullYear(), view.getMonth(), 1);
    const lead = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();
    for (let i = 0; i < lead; i++) {
      const pad = document.createElement('span');
      pad.className = 'ccdm-cal__day ccdm-cal__day--other';
      grid.appendChild(pad);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(view.getFullYear(), view.getMonth(), day);
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'ccdm-cal__day';
      b.textContent = String(day);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (date.getTime() === today.getTime()) b.classList.add('ccdm-cal__day--today');
      const ok = this._selectable(date);
      b.disabled = !ok;
      if (this._selectedDate && date.getTime() === this._selectedDate.getTime()) {
        b.classList.add('ccdm-cal__day--selected');
      }
      if (ok) {
        b.addEventListener('click', () => {
          this._selectedDate = date;
          this._date.value = date.toLocaleDateString('en-AU', {
            weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
          });
          this._showHint('');
          this._cal.hidden = true;
        });
      }
      grid.appendChild(b);
    }
    cal.appendChild(grid);
  }

  _formatSlot(date, timeLabel) {
    const day = date.toLocaleDateString('en-AU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
    return `${day} — ${timeLabel}`;
  }

  _addSlot() {
    if (this._slots.length >= MAX_SLOTS) {
      this._showHint(`Up to ${MAX_SLOTS} slots — remove one to add another.`);
      return;
    }
    const picked = this._selectedDate;
    if (!picked) {
      this._showHint('Pick a date first.');
      this._date?.focus();
      return;
    }
    const timeLabel = this._time?.value;
    if (!timeLabel) {
      this._showHint('Pick a one-hour slot.');
      this._time?.focus();
      return;
    }
    const label = this._formatSlot(picked, timeLabel);
    if (this._slots.includes(label)) {
      this._showHint('That slot is already on your list.');
      return;
    }
    this._slots.push(label);
    this._showHint('');
    this._renderSlots();
  }

  _removeSlot(label) {
    this._slots = this._slots.filter((s) => s !== label);
    this._renderSlots();
  }

  _renderSlots() {
    if (!this._chips) return;
    this._chips.textContent = '';
    this._slots.forEach((label, i) => {
      const chip = document.createElement('span');
      chip.className = 'ccdm-chip';
      const text = document.createElement('span');
      text.textContent = label;
      chip.appendChild(text);
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'ccdm-chip__remove';
      remove.setAttribute('aria-label', `Remove slot ${label}`);
      remove.textContent = '×';
      remove.addEventListener('click', () => this._removeSlot(label));
      chip.appendChild(remove);
      this._chips.appendChild(chip);
      const input = this._slotInputs[i];
      if (input) input.value = label;
    });
    for (let i = this._slots.length; i < this._slotInputs.length; i++) {
      this._slotInputs[i].value = '';
    }
    this._chips.hidden = this._slots.length === 0;
    if (this._addBtn) {
      this._addBtn.disabled = this._slots.length >= MAX_SLOTS;
    }
  }

  _guardSubmit(event) {
    // Convenience: a picked date+time that was never "added" still counts.
    if (this._slots.length === 0 && this._selectedDate && this._time?.value) {
      this._addSlot();
    }
    if (this._slots.length === 0) {
      event.preventDefault();
      this._showHint('Add at least one slot you can make so we can book you in.');
      this._date?.focus();
      return;
    }
    // The submit is a full-page POST — without feedback the dialog looks
    // frozen until the redirect lands. Show a pending state (deferred a
    // frame so the button still serializes into this submission) and block
    // double-submits.
    if (this._submitting) {
      event.preventDefault();
      return;
    }
    this._submitting = true;
    // Unused slot inputs would show as empty "Availability N:" rows in the
    // notification email; disabled controls are not serialized.
    this._slotInputs.forEach((input) => {
      if (!input.value) input.disabled = true;
    });
    const btn = this._form?.querySelector('.ccdm-submit');
    requestAnimationFrame(() => {
      if (btn) {
        btn.disabled = true;
        btn.textContent = 'Sending your request…';
      }
    });
  }

  _showHint(message) {
    if (!this._hint) return;
    this._hint.textContent = message;
    this._hint.hidden = !message;
  }

  /** Reparent the dialog onto document.body (see header comment). */
  _hostDialogOnBody() {
    if (this._dialogHosted || this._dialog.parentElement === document.body) return;
    this._dialogHome = {
      parent: this._dialog.parentNode,
      next: this._dialog.nextSibling,
    };
    document.body.appendChild(this._dialog);
    this._dialogHosted = true;
  }

  _open() {
    if (this._dialog.open) return;
    this._hostDialogOnBody();
    lockScroll(this._dialog);
    this._dialog.showModal();
  }

  async _close() {
    const dialog = this._dialog;
    if (!dialog?.open) return;
    dialog.classList.add('dialog-closing');
    await Promise.race([onAnimationEnd(dialog, undefined, { subtree: false }), wait(400)]);
    dialog.classList.remove('dialog-closing');
    unlockScroll(dialog);
    dialog.close();
  }
}

if (!customElements.get('ccc-demo-booking')) {
  customElements.define('ccc-demo-booking', DemoBooking);
}
